// src/tareas/TableroTareasPersonal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ✅ DnD + UI reutilizable
import KanbanUI from "./kanbanUI";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const ESTADOS = [
  { id: "pendiente", titulo: "Pendiente" },
  { id: "en_curso", titulo: "En curso" },
  { id: "resuelta", titulo: "Resuelta" },
  { id: "rechazada", titulo: "Rechazada" },
];

/* =========================================================
 *  ✅ Filtro: ocultar cerradas (resueltas/rechazadas) antiguas
 * ========================================================= */
const DIAS_OCULTAR_CERRADAS = 3;
const MS_DIA = 24 * 60 * 60 * 1000;
const ESTADOS_CERRADOS = new Set(["resuelta", "rechazada"]);

const parseDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const debeOcultarCerrada = (t, dias = DIAS_OCULTAR_CERRADAS, ahoraMs = Date.now()) => {
  const estado = String(t?.estado || "").toLowerCase();
  if (!ESTADOS_CERRADOS.has(estado)) return false;

  // ✅ Tu endpoint kanban devuelve p.fecha_ultima_actualizacion y p.fecha_creacion
  const dRef = parseDateSafe(t?.fecha_ultima_actualizacion) || parseDateSafe(t?.fecha_creacion);
  if (!dRef) return false; // fail-safe

  return ahoraMs - dRef.getTime() > dias * MS_DIA;
};

/* =========================================================
 *  Auth helpers
 * ========================================================= */
const authHeadersJson = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/* =========================================================
 *  Helpers UI (avatares)
 * ========================================================= */
const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return null;
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0][0] : "";
  const a1 = apellidos ? apellidos.split(" ")[0][0] : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

/* =========================================================
 *  Helpers UI (fechas / badges)
 * ========================================================= */
const formatearFechaCompleta = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const infoFechaVencimiento = (iso) => {
  if (!iso) return { texto: "", colorKey: "" };

  const fin = new Date(iso);
  const hoy = new Date();
  const msPorDia = 1000 * 60 * 60 * 24;
  const diff = Math.floor((fin - hoy) / msPorDia);

  if (Number.isNaN(diff)) return { texto: String(iso), colorKey: "" };

  if (diff < 0) {
    const dias = Math.abs(diff);
    const texto = dias === 0 ? "Vencida" : `Vencida hace ${dias} día${dias === 1 ? "" : "s"}`;
    return { texto, colorKey: "error" };
  }
  if (diff === 0) return { texto: "Vence hoy", colorKey: "warning" };
  if (diff === 1) return { texto: "Vence mañana", colorKey: "warning" };
  if (diff <= 7) return { texto: `Vence en ${diff} días`, colorKey: "warning" };

  return { texto: `Vence en ${diff} días`, colorKey: "success" };
};

const prioridadChip = (p) => {
  const pr = (p || "normal").toLowerCase();
  if (pr === "critica") return { label: "Crítica", color: "error" };
  if (pr === "alta") return { label: "Alta", color: "warning" };
  if (pr === "baja") return { label: "Baja", color: "success" };
  return { label: "Media", color: "default" };
};

const labelEstadoGlobal = (v) => {
  const s = (v || "").toString().toLowerCase();
  // Tu tareas.estado_global por defecto es 'abierta'
  if (s === "abierta") return "Abierta";
  if (s === "cerrada") return "Cerrada";
  if (s === "cancelada") return "Cancelada";
  if (s === "pendiente") return "Pendiente";
  if (s === "en_curso") return "En curso";
  if (s === "resuelta") return "Resuelta";
  if (s === "rechazada") return "Rechazada";
  return v || "-";
};

export const TableroTareasPersonal = ({ urlPersonalizada = null, mostrarTareasCreadas = true }) => {
  const toast = useToast();

  /* ---------------------------------------------
   *  Kanban: datos + carga
   * -------------------------------------------- */
  const [tareasPorEstado, setTareasPorEstado] = useState({});
  const [cargandoTablero, setCargandoTablero] = useState(true);

  /* ---------------------------------------------
   *  Usuarios (para avatares y nombres)
   * -------------------------------------------- */
  const [usuariosMapa, setUsuariosMapa] = useState({});

  /* ---------------------------------------------
   *  Departamentos (para resolver departamento_nombre en DataGrid)
   *  - best-effort: si no existe endpoint, no rompe
   * -------------------------------------------- */
  const [deptosMapa, setDeptosMapa] = useState({}); // { [id]: nombre }

  /* ---------------------------------------------
   *  Historial (modal por petición)
   * -------------------------------------------- */
  const [histModalAbierto, setHistModalAbierto] = useState(false);
  const [histCargando, setHistCargando] = useState(false);
  const [histEventos, setHistEventos] = useState([]);
  const [peticionSeleccionada, setPeticionSeleccionada] = useState(null);

  /* ---------------------------------------------
   *  ✅ Seguimiento de TAREA (modal tarea + peticiones)
   * -------------------------------------------- */
  const [segModalAbierto, setSegModalAbierto] = useState(false);
  const [segCargando, setSegCargando] = useState(false);
  const [segTarea, setSegTarea] = useState(null);
  const [segPeticiones, setSegPeticiones] = useState([]);

  /* ---------------------------------------------
   *  “Tareas creadas por mí” (DataGrid embebido)
   * -------------------------------------------- */
  const [mostrarListadoTareasCreadas, setMostrarListadoTareasCreadas] = useState(false);
  const [cargandoCreadas, setCargandoCreadas] = useState(false);
  const [tareasCreadasRaw, setTareasCreadasRaw] = useState([]);

  const [gridQuery, setGridQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  /* =========================================================
   *  Derivados
   * ========================================================= */
  const columnas = useMemo(() => ESTADOS, []);

  const inicializarEstado = (items) => {
    const base = {};
    ESTADOS.forEach((e) => (base[e.id] = []));
    (items || []).forEach((item) => {
      const estado = ESTADOS.some((e) => e.id === item.estado) ? item.estado : "pendiente";
      base[estado].push(item);
    });
    setTareasPorEstado(base);
  };

  /* =========================================================
   *  Cargas: usuarios + tablero + deptos (best effort)
   * ========================================================= */
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const res = await fetch(`${Global.url}usuarios/todos`, { headers: authHeaders() });
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) {
          const mapa = {};
          data.forEach((u) => (mapa[u.id] = u));
          setUsuariosMapa(mapa);
        }
      } catch (e) {
        console.error("Error cargando usuarios:", e);
      }
    };

    const fetchDeptos = async () => {
      try {
        // ⚠️ Si tu endpoint real es otro, cámbialo aquí.
        const res = await fetch(`${Global.url}departamentos`, { headers: authHeaders() });
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) {
          const mapa = {};
          data.forEach((d) => {
            if (d?.id != null) mapa[d.id] = d.nombre || d.descripcion || `Depto #${d.id}`;
          });
          setDeptosMapa(mapa);
        }
      } catch (e) {
        // best-effort
      }
    };

    fetchUsuarios();
    fetchDeptos();
  }, []);

  useEffect(() => {
    const fetchKanban = async () => {
      setCargandoTablero(true);
      try {
        const endpoint = urlPersonalizada || `${Global.url}tareas/tablero`;
        const res = await fetch(endpoint, { headers: authHeaders() });
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          console.error("Error al cargar tablero:", data);
          toast.error?.(data?.message || "No se pudo cargar el tablero");
          inicializarEstado([]);
          return;
        }

        // ✅ filtro cerradas antiguas
        const lista = Array.isArray(data) ? data : [];
        const filtradas = lista.filter((t) => !debeOcultarCerrada(t, DIAS_OCULTAR_CERRADAS));

        inicializarEstado(filtradas);
      } catch (e) {
        console.error("Error al cargar tablero:", e);
        toast.error?.("Error de red al cargar el tablero");
        inicializarEstado([]);
      } finally {
        setCargandoTablero(false);
      }
    };

    fetchKanban();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPersonalizada]);

  /* =========================================================
   *  Drag & Drop (Kanban)
   * ========================================================= */
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const peticionId = Number(draggableId);
    const nuevoEstado = destination.droppableId;

    // Optimista
    setTareasPorEstado((prev) => {
      const copy = {};
      ESTADOS.forEach((e) => (copy[e.id] = [...(prev[e.id] || [])]));

      const origenArr = copy[source.droppableId] || [];
      const destinoArr = copy[destination.droppableId] || [];

      const [movida] = origenArr.splice(source.index, 1);
      if (!movida) return prev;

      movida.estado = nuevoEstado;
      destinoArr.splice(destination.index, 0, movida);

      copy[source.droppableId] = origenArr;
      copy[destination.droppableId] = destinoArr;

      return copy;
    });

    // Persistir
    try {
      const res = await fetch(`${Global.url}tareas/peticiones/${peticionId}/estado`, {
        method: "PATCH",
        headers: authHeadersJson(),
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Error al actualizar estado:", data);
        toast.error?.(data?.message || "No se pudo actualizar el estado");
      } else {
        toast.success?.("Estado actualizado");
      }
    } catch (e) {
      console.error("Error al actualizar estado:", e);
      toast.error?.("Error de red al actualizar el estado");
    }
  };

  /* =========================================================
   *  Historial (modal por petición)
   * ========================================================= */
  const abrirHistorial = async (tareaPeticion) => {
    const peticionId = tareaPeticion.peticion_id;

    setPeticionSeleccionada(tareaPeticion);
    setHistEventos([]);
    setHistCargando(true);
    setHistModalAbierto(true);

    try {
      const res = await fetch(`${Global.url}peticiones/${peticionId}/historico`, {
        headers: authHeaders(),
      });

      const data = await res.json().catch(() => []);
      if (!res.ok) {
        console.error("Error al cargar historial:", data);
        toast.error?.(data?.message || "No se pudo cargar el historial");
        setHistEventos([]);
        return;
      }

      setHistEventos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar historial:", e);
      toast.error?.("Error de red al cargar el historial");
      setHistEventos([]);
    } finally {
      setHistCargando(false);
    }
  };

  const cerrarHistorial = () => {
    setHistModalAbierto(false);
    setPeticionSeleccionada(null);
    setHistEventos([]);
    setHistCargando(false);
  };

  /* =========================================================
   *  ✅ Seguimiento de TAREA (tarea + peticiones)
   * ========================================================= */
  const labelDestinoPeticion = (p) => {
    if (!p) return "—";

    if (p.tipo_destino === "usuario") {
      const u = usuariosMapa?.[p.destino_id];
      return u ? `${u.nombre || ""} ${u.apellidos || ""}`.trim() : `Usuario #${p.destino_id}`;
    }

    if (p.tipo_destino === "departamento") {
      const nombre = deptosMapa?.[p.destino_id];
      return nombre ? `Depto ${nombre}` : `Depto #${p.destino_id}`;
    }

    return `${p.tipo_destino || "Destino"} #${p.destino_id ?? "?"}`;
  };

  const abrirSeguimientoTarea = async (row) => {
    const tareaId = row?._raw?.id ?? row?.id;
    if (!tareaId) return;

    setSegModalAbierto(true);
    setSegCargando(true);
    setSegTarea(null);
    setSegPeticiones([]);

    try {
      const res = await fetch(`${Global.url}tareas/${tareaId}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Error cargando tarea+peticiones:", data);
        toast.error?.(data?.message || "No se pudo cargar el seguimiento de la tarea");
        setSegModalAbierto(false);
        return;
      }

      setSegTarea(data?.tarea || null);
      setSegPeticiones(Array.isArray(data?.peticiones) ? data.peticiones : []);
    } catch (e) {
      console.error("Error de red seguimiento tarea:", e);
      toast.error?.("Error de red al cargar el seguimiento de la tarea");
      setSegModalAbierto(false);
    } finally {
      setSegCargando(false);
    }
  };

  const cerrarSeguimientoTarea = () => {
    setSegModalAbierto(false);
    setSegCargando(false);
    setSegTarea(null);
    setSegPeticiones([]);
  };

  const abrirHistorialDesdeSeguimiento = (peticion) => {
    if (!peticion) return;

    // Reusamos modal historial: espera peticion_id y titulo
    abrirHistorial({
      peticion_id: peticion.id,
      titulo: segTarea?.titulo || "—",
      ...peticion,
    });
  };

  /* =========================================================
   *  Tareas creadas por mí (DataGrid): carga (lazy)
   * ========================================================= */
  const cargarTareasCreadas = async () => {
    setCargandoCreadas(true);
    try {
      const res = await fetch(`${Global.url}tareas/mis-creadas`, { headers: authHeaders() });
      const data = await res.json().catch(() => []);

      if (!res.ok) {
        console.error("Error al cargar creadas:", data);
        toast.error?.(data?.message || "No se pudo cargar tus tareas creadas");
        setTareasCreadasRaw([]);
        return;
      }

      setTareasCreadasRaw(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error red al cargar creadas:", e);
      toast.error?.("Error de red al cargar tus tareas creadas");
      setTareasCreadasRaw([]);
    } finally {
      setCargandoCreadas(false);
    }
  };

  useEffect(() => {
    if (!mostrarListadoTareasCreadas) return;
    if ((tareasCreadasRaw || []).length > 0) return;
    cargarTareasCreadas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarListadoTareasCreadas]);

  /* =========================================================
   *  DataGrid: normalización robusta
   * ========================================================= */
  const normaliza = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const tareasCreadasFiltradas = useMemo(() => {
    const q = normaliza(gridQuery);
    if (!q) return tareasCreadasRaw || [];

    return (tareasCreadasRaw || []).filter((t) => {
      const titulo = normaliza(t?.titulo);
      const desc = normaliza(t?.descripcion);
      const deptoId = normaliza(t?.departamento_id);
      const deptoNombre = normaliza(deptosMapa?.[t?.departamento_id] || "");
      const pr = normaliza(t?.prioridad);
      const estado = normaliza(t?.estado_global);

      return (
        titulo.includes(q) ||
        desc.includes(q) ||
        deptoId.includes(q) ||
        deptoNombre.includes(q) ||
        pr.includes(q) ||
        estado.includes(q)
      );
    });
  }, [tareasCreadasRaw, gridQuery, deptosMapa]);

  const rowsCreadas = useMemo(() => {
    return (tareasCreadasFiltradas || []).map((t, idx) => {
      const id = t?.id != null ? t.id : `tmp-${idx}`;

      const departamento_id = t?.departamento_id ?? null;
      const departamento_nombre =
        t?.departamento_nombre ??
        (departamento_id != null ? deptosMapa?.[departamento_id] || `Depto #${departamento_id}` : "-");

      const titulo = t?.titulo ?? "";
      const prioridad = t?.prioridad ?? "normal";
      const estado_global = t?.estado_global ?? "-";

      const creado_txt = formatearFechaCompleta(t?.fecha_creacion);
      const vence_txt = t?.fecha_vencimiento ? formatearFechaCompleta(t?.fecha_vencimiento) : "-";

      return {
        id,
        titulo,
        descripcion: t?.descripcion ?? "",
        departamento_id,
        departamento_nombre,
        prioridad,
        estado_global,

        fecha_creacion: t?.fecha_creacion ?? null,
        fecha_vencimiento: t?.fecha_vencimiento ?? null,

        creado_txt,
        vence_txt,

        total_peticiones: Number(t?.total_peticiones ?? 0),
        pet_pendientes: Number(t?.pet_pendientes ?? 0),
        pet_en_curso: Number(t?.pet_en_curso ?? 0),
        pet_resueltas: Number(t?.pet_resueltas ?? 0),
        pet_rechazadas: Number(t?.pet_rechazadas ?? 0),
        pet_canceladas: Number(t?.pet_canceladas ?? 0),

        _raw: t,
      };
    });
  }, [tareasCreadasFiltradas, deptosMapa]);

  const columnsCreadas = useMemo(
    () => [
      { field: "titulo", headerName: "Título", flex: 1, minWidth: 260, sortable: true },
      { field: "departamento_nombre", headerName: "Departamento", minWidth: 200, sortable: true },
      {
        field: "prioridad",
        headerName: "Prioridad",
        minWidth: 140,
        sortable: true,
        renderCell: (params) => {
          const chip = prioridadChip(params?.row?.prioridad);
          return <Chip size="small" label={chip.label} color={chip.color} />;
        },
      },
      {
        field: "estado_global",
        headerName: "Estado",
        minWidth: 170,
        sortable: true,
        valueFormatter: (v) => labelEstadoGlobal(v),
      },
      { field: "vence_txt", headerName: "Vence", minWidth: 190, sortable: true },
      { field: "creado_txt", headerName: "Creada", minWidth: 190, sortable: true },

      // ✅ NUEVO: Seguimiento (tarea + peticiones + historial)
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button size="small" onClick={() => abrirSeguimientoTarea(params.row)}>
            Seguimiento
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deptosMapa, usuariosMapa]
  );

  /* =========================================================
   *  Kanban: RenderCard
   * ========================================================= */
  const renderKanbanCard = (tarea) => {
    const creador = usuariosMapa[tarea.creador_id];
    const deptoNombre = tarea.departamento_nombre || null;

    const infoFecha = infoFechaVencimiento(tarea.fecha_vencimiento);
    const pChip = prioridadChip(tarea.prioridad);

    const canalLabel =
      tarea.tipo_destino === "departamento"
        ? deptoNombre
          ? `Vía departamento (${deptoNombre})`
          : "Vía departamento"
        : "Directa a ti";

    return (
      <Box sx={{ width: "100%" }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
          <Avatar
            src={creador && getAvatarUrl(creador) ? getAvatarUrl(creador) : undefined}
            sx={{ width: 34, height: 34, fontWeight: 800 }}
          >
            {getIniciales(creador || {})}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {creador ? `${creador.nombre || ""} ${creador.apellidos || ""}`.trim() : "Creador desconocido"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Creador
            </Typography>
          </Box>
        </Stack>

        <Typography sx={{ mt: 1, fontWeight: 800 }}>{tarea.titulo}</Typography>

        {deptoNombre && (
          <Box sx={{ mt: 0.75 }}>
            <Chip size="small" label={deptoNombre} />
          </Box>
        )}

        {tarea.descripcion && (
          <Typography sx={{ mt: 0.75, color: "text.secondary" }}>{tarea.descripcion}</Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={pChip.label} color={pChip.color} />
          {tarea.fecha_vencimiento && infoFecha?.texto ? (
            <Chip
              size="small"
              variant="outlined"
              label={`⏰ ${infoFecha.texto}`}
              color={infoFecha.colorKey || "default"}
              title={formatearFechaCompleta(tarea.fecha_vencimiento)}
            />
          ) : null}
        </Stack>

        <Typography variant="caption" sx={{ display: "block", mt: 1.1, color: "text.secondary" }}>
          Responsable: tú
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
          {canalLabel}
        </Typography>

        <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            size="small"
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={() => abrirHistorial(tarea)}
          >
            Historial
          </Button>
        </Box>
      </Box>
    );
  };

  /* =========================================================
   *  Render – Loading tablero
   * ========================================================= */
  if (cargandoTablero) {
    return (
      <Box className="appContainer appContainer--wide">
        <Stack spacing={2}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Tareas asignadas a mí
          </Typography>

          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: "text.secondary" }}>Cargando tablero…</Typography>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    );
  }

  /* =========================================================
   *  Render – Principal
   * ========================================================= */
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Tareas asignadas a mí
        </Typography>

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Nota: las tareas en “Resuelta” y “Rechazada” con más de {DIAS_OCULTAR_CERRADAS} días desde su última
          actualización se ocultan del tablero.
        </Typography>

        <KanbanUI
          columns={columnas.map((c) => ({ id: c.id, title: c.titulo }))}
          itemsByColumn={tareasPorEstado}
          getItemId={(item) => item.peticion_id}
          renderCard={renderKanbanCard}
          onDragEnd={onDragEnd}
        />

        {/* --------- “Tareas creadas por mí” (DataGrid embebido) --------- */}
        {mostrarTareasCreadas && (
          <Paper sx={{ p: 1.5 }}>
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 800 }}>Tareas creadas por mí</Typography>

              <Button
                size="small"
                onClick={() => setMostrarListadoTareasCreadas((v) => !v)}
                endIcon={mostrarListadoTareasCreadas ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {mostrarListadoTareasCreadas ? "Ocultar" : "Mostrar"}
              </Button>
            </Stack>

            {mostrarListadoTareasCreadas && (
              <Box sx={{ pt: 1 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  sx={{ alignItems: { sm: "center" }, mb: 1 }}
                >
                  <TextField
                    label="Buscar"
                    value={gridQuery}
                    onChange={(e) => {
                      setGridQuery(e.target.value);
                      setPage(0);
                    }}
                    placeholder="Título, descripción, depto, prioridad, estado…"
                    size="small"
                  />

                  <Box sx={{ flex: 1 }} />

                  <Button size="small" onClick={cargarTareasCreadas} disabled={cargandoCreadas}>
                    {cargandoCreadas ? "Actualizando..." : "Refrescar"}
                  </Button>
                </Stack>

                {cargandoCreadas ? (
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography sx={{ color: "text.secondary" }}>Cargando tareas creadas…</Typography>
                  </Stack>
                ) : (
                  <DataGrid
                    autoHeight
                    rows={rowsCreadas}
                    columns={columnsCreadas}
                    disableRowSelectionOnClick
                    pagination
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={(m) => {
                      setPage(m.page);
                      setPageSize(m.pageSize);
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    density="compact"
                    sx={{
                      "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                      "& .MuiDataGrid-cellContent": { width: "100%" },
                    }}
                  />
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* --------- Modal historial (por petición) --------- */}
        <Dialog open={histModalAbierto} onClose={cerrarHistorial} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800 }} noWrap>
                Historial de petición #{peticionSeleccionada?.peticion_id || ""}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                {peticionSeleccionada?.titulo || ""}
              </Typography>
            </Box>

            <IconButton onClick={cerrarHistorial} edge="end" aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 2 }}>
            {histCargando ? (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <CircularProgress size={18} />
                <Typography sx={{ color: "text.secondary" }}>Cargando historial…</Typography>
              </Stack>
            ) : histEventos.length === 0 ? (
              <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No hay histórico registrado para esta petición.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {histEventos.map((ev) => {
                  const usuarioNombre = `${ev.modificado_por_nombre || ""} ${ev.modificado_por_apellidos || ""}`.trim();

                  const idUsuarioEvento =
                    ev.modificado_por_id ?? ev.usuario_id ?? ev.modificado_por_usuario_id ?? null;

                  const usuarioEvento = idUsuarioEvento != null ? usuariosMapa[idUsuarioEvento] : null;
                  const srcAvatar =
                    usuarioEvento && getAvatarUrl(usuarioEvento) ? getAvatarUrl(usuarioEvento) : undefined;

                  const inicial =
                    usuarioEvento
                      ? getIniciales(usuarioEvento)
                      : (usuarioNombre || "?")[0]?.toUpperCase?.() || "?";

                  const hayCambioAsignacion =
                    ev.asignado_anterior_id !== null || ev.asignado_nuevo_id !== null;

                  const asignadoAnteriorNombre =
                    ev.asignado_anterior_nombre || ev.asignado_anterior_apellidos
                      ? `${ev.asignado_anterior_nombre || ""} ${ev.asignado_anterior_apellidos || ""}`.trim()
                      : "Sin responsable";

                  const asignadoNuevoNombre =
                    ev.asignado_nuevo_nombre || ev.asignado_nuevo_apellidos
                      ? `${ev.asignado_nuevo_nombre || ""} ${ev.asignado_nuevo_apellidos || ""}`.trim()
                      : "Sin responsable";

                  return (
                    <Paper key={ev.id} sx={{ p: 1.25, border: "1px solid rgba(0,0,0,0.08)" }}>
                      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
                        <Avatar src={srcAvatar} sx={{ width: 34, height: 34, fontWeight: 800 }}>
                          {inicial}
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {usuarioNombre || "Usuario desconocido"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {formatearFechaCompleta(ev.fecha_cambio)}
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography sx={{ mt: 1 }}>
                        {hayCambioAsignacion ? (
                          <>
                            Cambió la asignación de <b>{asignadoAnteriorNombre}</b> a <b>{asignadoNuevoNombre}</b>.
                          </>
                        ) : (
                          <>
                            Cambió el estado de <b>{ev.estado_anterior}</b> a <b>{ev.estado}</b>.
                          </>
                        )}
                      </Typography>

                      {ev.comentario && (
                        <Typography sx={{ mt: 0.75, color: "text.secondary" }}>
                          «{ev.comentario}»
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
        </Dialog>

        {/* --------- ✅ Modal Seguimiento (tarea + peticiones) --------- */}
        <Dialog open={segModalAbierto} onClose={cerrarSeguimientoTarea} fullWidth maxWidth="md">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800 }} noWrap>
                Seguimiento de tarea
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                {segTarea?.titulo || ""}
              </Typography>
            </Box>

            <IconButton onClick={cerrarSeguimientoTarea} edge="end" aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 2 }}>
            {segCargando ? (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <CircularProgress size={18} />
                <Typography sx={{ color: "text.secondary" }}>Cargando seguimiento…</Typography>
              </Stack>
            ) : !segTarea ? (
              <Typography sx={{ color: "text.secondary" }}>No se pudo cargar la tarea.</Typography>
            ) : (
              <Stack spacing={1.25}>
                <Paper sx={{ p: 1.25 }}>
                  <Typography variant="body2">
                    <b>Descripción:</b> {segTarea?.descripcion || "—"}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    <Chip size="small" label={`Prioridad: ${segTarea?.prioridad || "normal"}`} />
                    {segTarea?.fecha_vencimiento ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Vence: ${formatearFechaCompleta(segTarea.fecha_vencimiento)}`}
                      />
                    ) : null}
                  </Stack>
                </Paper>

                <Typography sx={{ fontWeight: 800, mt: 0.5 }}>
                  Peticiones asociadas ({segPeticiones.length})
                </Typography>

                {segPeticiones.length === 0 ? (
                  <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                    Esta tarea no tiene peticiones asociadas.
                  </Typography>
                ) : (
                  segPeticiones.map((p) => (
                    <Paper key={p.id} sx={{ p: 1.25, border: "1px solid rgba(0,0,0,0.08)" }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {labelDestinoPeticion(p)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Petición #{p.id} · Estado: <b>{p.estado}</b>
                            {" · "}Creada: {formatearFechaCompleta(p.fecha_creacion)}
                            {p.fecha_ultima_actualizacion
                              ? ` · Última act.: ${formatearFechaCompleta(p.fecha_ultima_actualizacion)}`
                              : ""}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            startIcon={<HistoryIcon fontSize="small" />}
                            onClick={() => abrirHistorialDesdeSeguimiento(p)}
                          >
                            Ver historial
                          </Button>
                        </Box>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default TableroTareasPersonal;
